import React from 'react';
import { MultiSelect } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import { MultiselectFieldProvider } from './MultiselectFieldContext';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_MULTI_STACK_GAP_CLASS,
  BESPOKE_CHECKBOX_CLASS,
  BESPOKE_OPTION_LABEL_CLASS,
  BESPOKE_OPTION_LABEL_DISABLED_CLASS,
  BESPOKE_CONTROL_ROW_CLASS,
  BESPOKE_EMPTY_STATE_CLASS,
  BESPOKE_EMPTY_STATE_CODE_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISMultiselectRenderProps {
  values: string[];
  onChange: (v: string[]) => void;
  onToggle: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}

export interface OASISMultiselectProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  hideLabel?: boolean;
  /** Outer `[data-qid]` wrapper — layout/grid placement (`min-w-0`, spans, …). */
  className?: string;
  style?: React.CSSProperties;
  /** Wraps prompt + checklist (default checklist `MultiSelect` or compound subtree). */
  innerClassName?: string;
  /** Classes appended to the field prompt label */
  labelClassName?: string;
  /**
   * Design-system checklist only (`compoundContent` omitted). Passed to `MultiSelect`.
   * Put grid/columns/gaps on layout nodes wrapping compound `children`, not here.
   */
  checklistClassName?: string;
  checklistStyle?: React.CSSProperties;
  optionRowClassName?: string;
  optionLabelClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  checkboxClassName?: string;
  children?: (props: OASISMultiselectRenderProps) => React.ReactNode;
  /** Compound bespoke layout: subtree provides its own wrappers + `repeat` / `OASISMultiselectOption`. */
  compoundContent?: React.ReactNode;
}

export function OASISMultiselect({
  qid,
  labelOverride,
  className,
  style,
  innerClassName,
  labelClassName,
  checklistClassName,
  checklistStyle,
  optionRowClassName,
  optionLabelClassName,
  controlClassName,
  checkboxClassName,
  hideLabel,
  children,
  compoundContent,
}: OASISMultiselectProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const values = (answers[qid] as string[]) ?? [];
  const setValues = (v: string[]) => onAnswerChange(qid, v);
  const onChange = setValues;
  const onToggle = (v: string) => {
    const next = values.includes(v)
      ? values.filter(x => x !== v)
      : [...values, v];
    onAnswerChange(qid, next);
  };

  const options = (question?.values ?? []).map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;

  const renderProps: OASISMultiselectRenderProps = {
    values,
    onChange,
    onToggle,
    options,
    label,
  };

  const compound = compoundContent != null;

  const fieldCtx = {
    qid,
    values,
    options,
    toggle: onToggle,
    setValues,
    disabled: inheritedDisabled,
  };

  const inner = innerClassName ?? BESPOKE_MULTI_STACK_GAP_CLASS;
  const promptLabelCls = joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName);
  const resolvedCheckboxClassName = joinClasses(
    BESPOKE_CHECKBOX_CLASS,
    resolveControlClassName(controlClassName, checkboxClassName)
  );
  const resolvedOptionLabelClassName = joinClasses(
    BESPOKE_OPTION_LABEL_CLASS,
    inheritedDisabled && BESPOKE_OPTION_LABEL_DISABLED_CLASS,
    optionLabelClassName
  );
  const resolvedOptionRowClassName = joinClasses(
    BESPOKE_CONTROL_ROW_CLASS,
    optionRowClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children(renderProps)
      ) : compound ? (
        <div className={inner}>
          {!hideLabel && <label className={promptLabelCls}>{label}</label>}
          <MultiselectFieldProvider value={fieldCtx}>
            {compoundContent}
          </MultiselectFieldProvider>
        </div>
      ) : (
        <div className={inner}>
          {!hideLabel && <label className={promptLabelCls}>{label}</label>}
          {options.length === 0 ? (
            <p className={BESPOKE_EMPTY_STATE_CLASS}>
              No checklist options in loaded schema for{' '}
              <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>{qid}</code>.
              Select a RogerDoc schema + section that define this multiselect.
            </p>
          ) : (
            <MultiSelect
              disabled={inheritedDisabled}
              className={checklistClassName}
              style={checklistStyle}
              options={options}
              value={values}
              onChange={onChange}
              idPrefix={`${qid}-`}
              checkboxClassName={resolvedCheckboxClassName}
              optionLabelClassName={resolvedOptionLabelClassName}
              optionRowClassName={resolvedOptionRowClassName}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Vibecode authoring notes for `OASISMultiselect` nodes (empty = omit from LLM bundle). */
export const OASIS_MULTISELECT_VIBECODE =
  '`OASISMultiselect`: optional `children` for compound layout (grid/table/repeat+option) vs default checklist.';
